import React, { useState, useEffect } from "react";
import "./Reports.css";
import { backend_url, currency } from "../../App";
import * as XLSX from "xlsx";

const Reports = () => {
  const [activeCategory, setActiveCategory] = useState("sales");
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1))
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });
  const [reportData, setReportData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reportCategories = [
    {
      id: "sales",
      name: "Sales & Revenue",
      icon: "💰",
      reports: [
        {
          id: "daily-sales",
          name: "Daily/Weekly/Monthly Sales Reports",
          endpoint: "/admin/reports/sales",
        },
        {
          id: "product-performance",
          name: "Product Performance Reports",
          endpoint: "/admin/reports/product-performance",
        },
        {
          id: "payment-reports",
          name: "Payment Reports",
          endpoint: "/admin/reports/payments",
        },
      ],
    },
    {
      id: "orders",
      name: "Order Management",
      icon: "📦",
      reports: [
        {
          id: "order-status",
          name: "Order Status Reports",
          endpoint: "/admin/reports/order-status",
        },
        {
          id: "order-analytics",
          name: "Order Analytics",
          endpoint: "/admin/reports/order-analytics",
        },
      ],
    },
    {
      id: "customers",
      name: "Customer Reports",
      icon: "👥",
      reports: [
        {
          id: "customer-reports",
          name: "Customer Reports",
          endpoint: "/admin/reports/customers",
        },
        {
          id: "customer-analytics",
          name: "Customer Analytics",
          endpoint: "/admin/reports/customer-analytics",
        },
        {
          id: "customer-behavior",
          name: "Customer Behavior Reports",
          endpoint: "/admin/reports/customer-behavior",
        },
      ],
    },
    {
      id: "inventory",
      name: "Inventory Reports",
      icon: "📊",
      reports: [
        {
          id: "stock-management",
          name: "Stock Management",
          endpoint: "/admin/reports/stock",
        },
        {
          id: "product-management",
          name: "Product Management",
          endpoint: "/admin/reports/product-management",
        },
      ],
    },
    {
      id: "financial",
      name: "Financial Reports",
      icon: "💹",
      reports: [
        {
          id: "profit-loss",
          name: "Profit & Loss Reports",
          endpoint: "/admin/reports/profit-loss",
        },
        {
          id: "tax-compliance",
          name: "Tax & Compliance Reports",
          endpoint: "/admin/reports/tax-compliance",
        },
      ],
    },
    {
      id: "marketing",
      name: "Marketing & Traffic",
      icon: "📈",
      reports: [
        {
          id: "website-analytics",
          name: "Website Analytics",
          endpoint: "/admin/reports/website-analytics",
        },
        {
          id: "campaign-performance",
          name: "Campaign Performance",
          endpoint: "/admin/reports/campaigns",
        },
      ],
    },
    {
      id: "administrative",
      name: "Administrative",
      icon: "⚙️",
      reports: [
        {
          id: "user-management",
          name: "User Management",
          endpoint: "/admin/reports/user-management",
        },
        {
          id: "system-performance",
          name: "System Performance",
          endpoint: "/admin/reports/system-performance",
        },
      ],
    },
    {
      id: "operational",
      name: "Operational",
      icon: "🚚",
      reports: [
        {
          id: "shipping-logistics",
          name: "Shipping & Logistics",
          endpoint: "/admin/reports/shipping",
        },
        {
          id: "customer-service",
          name: "Customer Service",
          endpoint: "/admin/reports/customer-service",
        },
      ],
    },
  ];

  const fetchReportData = async (reportId, endpoint) => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${backend_url}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "auth-token": localStorage.getItem("auth-token"),
        },
        body: JSON.stringify({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setReportData((prev) => ({
          ...prev,
          [reportId]: data.data,
        }));
      } else {
        throw new Error(data.message || "Failed to fetch report data");
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      setError(`Failed to fetch ${reportId} data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = (reportId, data, reportName) => {
    if (!data || data.length === 0) {
      alert("No data available to export");
      return;
    }

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, reportName);

    // Auto-size columns
    const colWidths = [];
    data.forEach((row) => {
      Object.keys(row).forEach((key, index) => {
        const length = Math.max(
          row[key] ? row[key].toString().length : 0,
          key.length
        );
        colWidths[index] = Math.max(colWidths[index] || 0, length);
      });
    });

    ws["!cols"] = colWidths.map((width) => ({
      width: Math.min(width + 2, 50),
    }));

    const fileName = `${reportName.replace(/\s+/g, "_")}_${
      dateRange.startDate
    }_to_${dateRange.endDate}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const renderReportContent = (report) => {
    const data = reportData[report.id];

    if (!data) {
      return (
        <div className="report-placeholder">
          <p>Click "Generate Report" to load data</p>
        </div>
      );
    }

    if (data.length === 0) {
      return (
        <div className="report-placeholder">
          <p>No data available for the selected date range</p>
        </div>
      );
    }

    return (
      <div className="report-content">
        <div className="report-summary">
          <h4>Summary</h4>
          <div className="summary-stats">
            <div className="stat-item">
              <span className="stat-label">Total Records:</span>
              <span className="stat-value">{data.length}</span>
            </div>
            {report.id === "daily-sales" && data.length > 0 && (
              <>
                <div className="stat-item">
                  <span className="stat-label">Total Revenue:</span>
                  <span className="stat-value">
                    {currency}
                    {data
                      .reduce((sum, item) => sum + (item.total_amount || 0), 0)
                      .toFixed(2)}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Average Order Value:</span>
                  <span className="stat-value">
                    {currency}
                    {(
                      data.reduce(
                        (sum, item) => sum + (item.total_amount || 0),
                        0
                      ) / data.length
                    ).toFixed(2)}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="report-table">
          <table>
            <thead>
              <tr>
                {Object.keys(data[0]).map((key) => (
                  <th key={key}>{key.replace(/_/g, " ").toUpperCase()}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.slice(0, 100).map((row, index) => (
                <tr key={index}>
                  {Object.entries(row).map(([key, value], cellIndex) => (
                    <td key={cellIndex}>
                      {typeof value === "number" && key.includes("amount")
                        ? `${currency}${value.toFixed(2)}`
                        : value instanceof Date
                        ? value.toLocaleDateString()
                        : value?.toString() || "N/A"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {data.length > 100 && (
            <p className="table-note">
              Showing first 100 records. Export to Excel for complete data.
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="reports-container">
      <div className="reports-header">
        <h1>Reports Dashboard</h1>
        <div className="date-range-selector">
          <label>
            From:
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, startDate: e.target.value }))
              }
            />
          </label>
          <label>
            To:
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) =>
                setDateRange((prev) => ({ ...prev, endDate: e.target.value }))
              }
            />
          </label>
        </div>
      </div>

      <div className="reports-content">
        <div className="reports-sidebar">
          <h3>Report Categories</h3>
          {reportCategories.map((category) => (
            <div
              key={category.id}
              className={`category-item ${
                activeCategory === category.id ? "active" : ""
              }`}
              onClick={() => setActiveCategory(category.id)}
            >
              <span className="category-icon">{category.icon}</span>
              <span className="category-name">{category.name}</span>
            </div>
          ))}
        </div>

        <div className="reports-main">
          {error && <div className="error-message">{error}</div>}

          {reportCategories
            .find((cat) => cat.id === activeCategory)
            ?.reports.map((report) => (
              <div key={report.id} className="report-section">
                <div className="report-header">
                  <h3>{report.name}</h3>
                  <div className="report-actions">
                    <button
                      className="generate-btn"
                      onClick={() =>
                        fetchReportData(report.id, report.endpoint)
                      }
                      disabled={loading}
                    >
                      {loading ? "Generating..." : "Generate Report"}
                    </button>
                    {reportData[report.id] && (
                      <button
                        className="export-btn"
                        onClick={() =>
                          exportToExcel(
                            report.id,
                            reportData[report.id],
                            report.name
                          )
                        }
                      >
                        Export to Excel
                      </button>
                    )}
                  </div>
                </div>

                {renderReportContent(report)}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default Reports;
